import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { demarcheAPI } from '../../service/api';
import { resolveUploadUrl } from '../../config/config';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import { confirmAction, promptReason, showError, showSuccess, toast } from '../../service/swal';
import {
  FiFileText, FiClock, FiCheckCircle, FiXCircle, FiEye,
  FiSearch, FiDownload, FiPaperclip, FiCalendar, FiUser, FiPlus, FiUpload
} from 'react-icons/fi';

const stepLabels = {
  visa: 'Visa étudiant', academic: 'Inscription académique',
  insurance: 'Assurance', residence_permit: 'Carte de séjour',
};

const statusConfig = {
  en_attente: { icon: FiClock, label: 'EN ATTENTE', color: '#D4A017', bg: '#FFF8E0' },
  en_cours: { icon: FiFileText, label: 'EN COURS', color: '#1565C0', bg: '#E3F0FF' },
  valide: { icon: FiCheckCircle, label: 'VALIDÉ', color: '#2E7D32', bg: '#E8F5E9' },
  rejete: { icon: FiXCircle, label: 'REJETÉ', color: '#C62828', bg: '#FFEBEE' },
};

const fileIcons = {
  pdf: '📄', jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️',
  doc: '📝', docx: '📝', xls: '📊', xlsx: '📊',
};
function getFileIcon(filename) {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return fileIcons[ext] || '📎';
}

export default function ServiceAcadDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState([]);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [viewStep, setViewStep] = useState(null);
  const [stepDetail, setStepDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const adminFileInputRef = useRef(null);

  const fetchStats = useCallback(async (skipLoading = false) => {
    try {
      const r = await demarcheAPI.getStats();
      setStats(r.data.data || []);
    } catch {}
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useAutoRefresh(fetchStats, 30000);

  const fetchSteps = useCallback(async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try {
      const params = { page: 1, limit: 100 };
      if (filter) params.statutCourant = filter;
      if (typeFilter) params.type = typeFilter;
      if (search) params.search = search;
      const res = await demarcheAPI.getAllAdmin(params);
      setSteps(res.data.steps || []);
    } catch (err) { console.error(err); }
    finally { if (!skipLoading) setLoading(false); }
  }, [filter, typeFilter, search]);

  useEffect(() => { fetchSteps(); }, [fetchSteps]);
  useAutoRefresh(() => fetchSteps(true), 30000);

  const handleAdminFileUpload = async (stepId, files) => {
    const formData = new FormData();
    for (const f of files) formData.append('fichiers', f);
    try {
      await demarcheAPI.uploadDocument(stepId, formData);
      const r = await demarcheAPI.getById(stepId);
      setStepDetail(r.data.data);
      toast('success', 'Document ajouté');
    } catch (err) { showError('Erreur lors de l\'upload'); }
  };

  const handleStatus = async (id, statutCourant) => {
    let commentaire = '';
    if (statutCourant === 'rejete') {
      commentaire = await promptReason('Motif du rejet');
      if (commentaire === null) return;
    }
    try { await demarcheAPI.updateStatus(id, { statutCourant, commentaire }); fetchSteps(); toast('success', 'Statut mis à jour'); }
    catch (err) { showError('Erreur lors de la mise à jour'); }
  };

  const openDossier = async (id) => {
    setViewStep(id);
    setDetailLoading(true);
    try { const r = await demarcheAPI.getById(id); setStepDetail(r.data.data); }
    catch (err) { console.error(err); }
    finally { setDetailLoading(false); }
  };

  const total = stats.reduce((a, s) => a + (s.total || 0), 0);
  const enAttente = stats.reduce((a, s) => a + (s.en_attente || 0), 0);
  const enCours = stats.reduce((a, s) => a + (s.en_cours || 0), 0);

  return (
    <div className="page-container py-8">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-14 h-14 flex items-center justify-center text-xl font-bold" style={{ backgroundColor: '#F4F4F0', color: '#0A192F' }}>
          {user?.nom?.charAt(0) || 'S'}
        </div>
        <div>
          <h1 className="font-serif text-2xl font-bold tracking-tight" style={{ color: '#0A192F' }}>Service Académique</h1>
          <p className="text-sm" style={{ color: '#7A828A' }}>Gestion des dossiers étudiants — {user?.nom}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-8">
        {[
          { icon: FiFileText, label: 'TOTAL DOSSIERS', value: total, color: '#0A192F' },
          { icon: FiClock, label: 'EN ATTENTE', value: enAttente, color: '#D4A017' },
          { icon: FiCheckCircle, label: 'EN COURS', value: enCours, color: '#1565C0' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="card-root p-5">
            <Icon className="w-6 h-6 mb-2" style={{ color }} />
            <p className="text-2xl font-bold" style={{ color: '#0A192F' }}>{value}</p>
            <p className="label-root mt-1">{label}</p>
          </div>
        ))}
      </div>

      <div className="card-root mb-6">
        <div className="p-4 flex flex-col sm:flex-row gap-4" style={{ borderBottom: '2px solid #E5E5E0' }}>
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#7A828A' }} />
            <input className="input-root pl-10" placeholder="Rechercher par étudiant..." value={search}
              onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className="input-root sm:w-40" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">Tous les types</option>
            {Object.entries(stepLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select className="input-root sm:w-40" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">Tous les statuts</option>
            <option value="en_attente">En attente</option>
            <option value="en_cours">En cours</option>
            <option value="valide">Validé</option>
            <option value="rejete">Rejeté</option>
          </select>
        </div>

        {loading ? (
          <div className="p-12 text-center text-sm" style={{ color: '#7A828A' }}>Chargement...</div>
        ) : steps.length === 0 ? (
          <div className="p-12 text-center text-sm" style={{ color: '#7A828A' }}>Aucun dossier trouvé</div>
        ) : (
          <div className="divide-y" style={{ borderColor: '#E5E5E0' }}>
            {steps.map((s) => {
              const cfg = statusConfig[s.statutCourant] || statusConfig.en_attente;
              return (
                <div key={s.id} className="p-4 flex items-center justify-between hover:bg-[#FAFAF6] transition-colors">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div>
                      <p className="font-bold text-sm" style={{ color: '#0A192F' }}>{s.studentName}</p>
                      <p className="text-xs" style={{ color: '#7A828A' }}>{s.numeroPasseport}</p>
                    </div>
                    <span className="text-sm font-medium" style={{ color: '#0A192F' }}>{stepLabels[s.type] || s.type}</span>
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold tracking-widest uppercase px-3 py-1 border-2"
                      style={{ color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}>
                      <cfg.icon className="w-3 h-3" /> {cfg.label}
                    </span>
                    <span className="text-xs" style={{ color: '#7A828A' }}>
                      {s.documentCount || 0} doc. | {new Date(s.dateCreation).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button className="btn-root text-xs px-4 py-2" style={{ borderColor: '#0A192F', color: '#0A192F' }}
                      onClick={() => openDossier(s.id)}>
                      <FiEye className="mr-1 w-3 h-3" /> Dossier
                    </button>
                    {s.statutCourant !== 'valide' && (
                      <button className="btn-root text-xs px-3 py-2" style={{ borderColor: '#2E7D32', color: '#2E7D32' }}
                        onClick={() => handleStatus(s.id, 'valide')}>
                        <FiCheckCircle className="w-3 h-3" />
                      </button>
                    )}
                    {s.statutCourant !== 'rejete' && s.statutCourant !== 'valide' && (
                      <button className="btn-root text-xs px-3 py-2" style={{ borderColor: '#C62828', color: '#C62828' }}
                        onClick={() => handleStatus(s.id, 'rejete')}>
                        <FiXCircle className="w-3 h-3" />
                      </button>
                    )}
                    {s.statutCourant === 'en_attente' && (
                      <button className="btn-root text-xs px-3 py-2" style={{ borderColor: '#1565C0', color: '#1565C0' }}
                        onClick={() => handleStatus(s.id, 'en_cours')}>
                        <FiClock className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {viewStep && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setViewStep(null)}
          style={{ animation: 'fadeIn 0.15s ease' }}>
          <div className="bg-white w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ border: '2px solid #0A192F', animation: 'slideUp 0.2s ease' }}>
            <div className="sticky top-0 bg-white z-10" style={{ borderBottom: '2px solid #E5E5E0' }}>
              <div className="flex items-center justify-between p-6">
                <div className="flex items-center gap-3">
                  <h3 className="font-serif font-bold text-lg" style={{ color: '#0A192F' }}>
                    Dossier {stepLabels[stepDetail?.type] || stepDetail?.type}
                  </h3>
                  {stepDetail && (
                    <span className="text-[11px] font-bold tracking-widest uppercase px-3 py-1 border-2"
                      style={{ color: (statusConfig[stepDetail.statutCourant] || {}).color, borderColor: (statusConfig[stepDetail.statutCourant] || {}).color, backgroundColor: (statusConfig[stepDetail.statutCourant] || {}).bg }}>
                      {(statusConfig[stepDetail.statutCourant] || {}).label}
                    </span>
                  )}
                </div>
                <button className="text-2xl" style={{ color: '#7A828A' }} onClick={() => setViewStep(null)}>✕</button>
              </div>
              {stepDetail && (
                <div className="px-6 pb-4 flex items-center gap-6 text-sm" style={{ color: '#5A626A' }}>
                  <div className="flex items-center gap-2">
                    <FiUser className="w-4 h-4" style={{ color: '#7A828A' }} />
                    <span className="font-semibold" style={{ color: '#0A192F' }}>{stepDetail.studentName}</span>
                  </div>
                  {stepDetail.numeroPasseport && (
                    <span className="text-xs" style={{ color: '#7A828A' }}>Passeport: {stepDetail.numeroPasseport}</span>
                  )}
                  {stepDetail.nationalite && (
                    <span className="text-xs" style={{ color: '#7A828A' }}>{stepDetail.nationalite}</span>
                  )}
                  <span className="text-xs" style={{ color: '#7A828A' }}>
                    Créé le {new Date(stepDetail.dateCreation).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>

            {detailLoading ? (
              <div className="p-12 text-center text-sm" style={{ color: '#7A828A' }}>Chargement...</div>
            ) : stepDetail ? (
              <div className="p-6 space-y-8">

                <div>
                  <h4 className="text-[11px] font-bold tracking-widest uppercase mb-3 flex items-center gap-2" style={{ color: '#7A828A' }}>
                    <FiPaperclip className="w-4 h-4" /> Documents soumis ({stepDetail.documents?.length || 0})
                  </h4>
                  {(!stepDetail.documents || stepDetail.documents.length === 0) ? (
                    <div className="py-8 text-center" style={{ border: '2px dashed #E5E5E0' }}>
                      <FiFileText className="w-10 h-10 mx-auto mb-2" style={{ color: '#E5E5E0' }} />
                      <p className="text-sm" style={{ color: '#7A828A' }}>Aucun document soumis</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {(stepDetail.documents || []).map((doc) => {
                        const isImage = /\.(jpg|jpeg|png|gif)$/i.test(doc.fichierUrl);
                        return (
                          <div key={doc.id} className="p-3" style={{ border: '2px solid #E5E5E0', backgroundColor: '#FAFAF6' }}>
                            <div className="flex items-start gap-3">
                              <span className="text-2xl">{isImage ? '🖼️' : getFileIcon(doc.nomFichier)}</span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold truncate" style={{ color: '#0A192F' }}
                                  title={doc.nomFichier || ''}>{doc.nomFichier || 'Sans nom'}</p>
                                <span className={`inline-block text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 mt-1 ${doc.typeDocument === 'document_admin' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {doc.typeDocument === 'document_admin' ? 'Administration' : 'Étudiant'}
                                </span>
                                <p className="text-xs mt-1" style={{ color: '#7A828A' }}>
                                  {new Date(doc.dateTelechargement).toLocaleDateString()}
                                </p>
                                <div className="flex gap-2 mt-2">
                                  <a href={resolveUploadUrl(doc.fichierUrl)} target="_blank" rel="noopener noreferrer"
                                    className="btn-root text-xs px-3 py-1" style={{ borderColor: '#0A192F', color: '#0A192F', fontSize: '10px' }}>
                                    <FiEye className="w-3 h-3 mr-1" /> Voir
                                  </a>
                                  <a href={resolveUploadUrl(doc.fichierUrl)} download
                                    className="btn-root text-xs px-3 py-1" style={{ borderColor: '#0A192F', color: '#0A192F', fontSize: '10px' }}>
                                    <FiDownload className="w-3 h-3 mr-1" /> Télécharger
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-3">
                    <input type="file" multiple ref={adminFileInputRef}
                      onChange={(e) => { if (e.target.files.length) handleAdminFileUpload(stepDetail.id, e.target.files); }}
                      className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                    <button className="btn-root text-xs px-3 py-1 flex items-center gap-1"
                      style={{ borderColor: '#0A192F', color: '#0A192F' }}
                      onClick={() => adminFileInputRef.current?.click()}>
                      <FiUpload className="w-3 h-3" /> Ajouter un document
                    </button>
                  </div>
                </div>

                <div>
                  <h4 className="text-[11px] font-bold tracking-widest uppercase mb-3 flex items-center gap-2" style={{ color: '#7A828A' }}>
                    <FiCalendar className="w-4 h-4" /> Historique du dossier
                  </h4>
                  {(stepDetail.history || []).length === 0 ? (
                    <div className="py-8 text-center" style={{ border: '2px dashed #E5E5E0' }}>
                      <FiClock className="w-10 h-10 mx-auto mb-2" style={{ color: '#E5E5E0' }} />
                      <p className="text-sm" style={{ color: '#7A828A' }}>Aucun historique</p>
                    </div>
                  ) : (
                    <div className="space-y-0">
                      {(stepDetail.history || []).map((h, i) => {
                        const hCfg = statusConfig[h.statut] || statusConfig.en_attente;
                        const isLast = i === (stepDetail.history || []).length - 1;
                        return (
                          <div key={h.id} className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: hCfg.color }} />
                              {!isLast && <div className="w-0.5 flex-1" style={{ backgroundColor: '#E5E5E0' }} />}
                            </div>
                            <div className="pb-4">
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] font-bold tracking-widest uppercase" style={{ color: hCfg.color }}>
                                  {hCfg.label}
                                </span>
                                <span className="text-xs" style={{ color: '#7A828A' }}>
                                  {new Date(h.dateModification).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              {h.commentaire && (
                                <p className="text-sm mt-1" style={{ color: '#5A626A' }}>{h.commentaire}</p>
                              )}
                              {h.modifieParNom && (
                                <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: '#7A828A' }}>
                                  <FiUser className="w-3 h-3" /> Par {h.modifieParNom}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3 pt-4" style={{ borderTop: '2px solid #E5E5E0' }}>
                  {stepDetail.statutCourant !== 'valide' && (
                    <button className="btn-root px-5 py-3 text-sm font-semibold"
                      style={{ borderColor: '#2E7D32', color: '#2E7D32' }}
                      onClick={async () => {
                        const confirmed = await confirmAction('Valider ce dossier ?', 'Le statut passera à "Validé".');
                        if (!confirmed) return;
                        await demarcheAPI.updateStatus(stepDetail.id, { statutCourant: 'valide' });
                        setViewStep(null); fetchSteps(); toast('success', 'Dossier validé');
                      }}>
                      <FiCheckCircle className="mr-2 w-4 h-4" /> Valider le dossier
                    </button>
                  )}
                  {stepDetail.statutCourant !== 'rejete' && stepDetail.statutCourant !== 'valide' && (
                    <button className="btn-root px-5 py-3 text-sm font-semibold"
                      style={{ borderColor: '#C62828', color: '#C62828' }}
                      onClick={async () => {
                        const c = await promptReason('Motif du rejet');
                        if (c) { await demarcheAPI.updateStatus(stepDetail.id, { statutCourant: 'rejete', commentaire: c }); setViewStep(null); fetchSteps(); toast('success', 'Dossier rejeté'); }
                      }}>
                      <FiXCircle className="mr-2 w-4 h-4" /> Rejeter le dossier
                    </button>
                  )}
                  {stepDetail.statutCourant === 'en_attente' && (
                    <button className="btn-root px-5 py-3 text-sm font-semibold"
                      style={{ borderColor: '#1565C0', color: '#1565C0' }}
                      onClick={async () => {
                        await demarcheAPI.updateStatus(stepDetail.id, { statutCourant: 'en_cours' });
                        setViewStep(null); fetchSteps();
                      }}>
                      <FiClock className="mr-2 w-4 h-4" /> Marquer en cours
                    </button>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
